import { trackAnalyticsEvent } from 'util/analytics';
import React, { ReactElement, useState } from 'react';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { Box } from '@mui/material';
import { useOrganizationsApi } from 'services/api';
import {
  ListMyOrganizationsResponse,
  MyOrganization,
} from 'services/api/apis/organizations/dto/my-organizations.dto';
import { Routes } from 'services/routes';
import { OrganizationEventsModal } from 'components/ui';
import { SectionHeader, EmptyCard, OrgRole } from '../shared';
import { OrgTile, OrgTileSkeleton } from './OrgTile';

export interface YourOrgsProps {
  hideHeader?: boolean;
  compact?: boolean;
}

interface SelectedOrg {
  id: string;
  name: string;
  logoUrl: string;
  schoolSlug: string;
}

const resolveRole = (org: MyOrganization): OrgRole => {
  if (org.myRole === 'admin') return 'admin';
  if (org.myRole === 'leader') return 'leader';
  if (org.myRole === 'member') return 'member';
  return 'follower';
};

export const YourOrgs = ({
  hideHeader = false,
  compact = false,
}: YourOrgsProps): ReactElement => {
  const router = useRouter();
  const organizationsApi = useOrganizationsApi();
  const [selectedOrg, setSelectedOrg] = useState<SelectedOrg | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const { data, isValidating, mutate } = useSWR<ListMyOrganizationsResponse>(
    'for-you/my-organizations',
    async () => {
      const res = await organizationsApi.getMyOrganizations();
      return res.success ? res.value : { organizations: [] };
    },
  );

  const orgs = data?.organizations ?? [];
  const isLoading = !data && isValidating;

  const openView = (org: MyOrganization): void => {
    const role = resolveRole(org);
    trackAnalyticsEvent({
      name: 'my_org_card_clicked',
      props: {
        role: role === 'follower' ? 'member' : role,
        action: 'view',
      },
    });
    setSelectedOrg({
      id: org.id,
      name: org.name,
      logoUrl: org.logoUrl,
      schoolSlug: org.school.slug,
    });
    setModalOpen(true);
  };

  const openManage = (org: MyOrganization): void => {
    trackAnalyticsEvent({
      name: 'my_org_card_clicked',
      props: { role: org.myRole, action: 'manage' },
    });
    void router.push(Routes.admin.organizationDetail(org.id));
  };

  return (
    <Box width="100%">
      {!hideHeader && (
        <SectionHeader
          eyebrow="FOLLOWING"
          count={isLoading ? undefined : orgs.length}
          title="Your organizations"
          titleSize={24}
        />
      )}
      <Box
        mt={hideHeader ? 0 : '16px'}
        sx={{
          display: 'grid',
          gridTemplateColumns: compact
            ? {
                xs: '1fr',
                md: 'repeat(auto-fill, 358px)',
              }
            : {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
              },
          gap: compact ? '16px' : '32px',
          alignItems: 'stretch',
          mx: compact ? 0 : { xs: '-20px', md: 0 },
        }}
      >
        {isLoading ? (
          [0, 1, 2].map(i => (
            <Box key={i} sx={{ width: '100%', maxWidth: '100%' }}>
              <OrgTileSkeleton />
            </Box>
          ))
        ) : orgs.length === 0 ? (
          <Box sx={{ width: '100%', gridColumn: '1 / -1' }}>
            <EmptyCard
              icon="groups"
              title="Find your people."
              body="Clubs, Greek life, sports, faith groups — there's something on campus for everyone."
              cta={{
                label: 'Explore orgs',
              onClick: () => {
                void router.replace(
                  {
                    pathname: router.pathname,
                    query: { ...router.query, tab: 'organizations' },
                  },
                  undefined,
                  { shallow: true },
                );
              },
              }}
            />
          </Box>
        ) : (
          orgs.map(org => (
            <Box key={org.id} sx={{ width: '100%', height: '100%', maxWidth: '100%' }}>
              <OrgTile
                name={org.name}
                logoUrl={org.logoUrl}
                role={resolveRole(org)}
                onView={() => openView(org)}
                onManage={
                  org.myRole === 'admin' || org.myRole === 'leader'
                    ? () => openManage(org)
                    : undefined
                }
              />
            </Box>
          ))
        )}
      </Box>

      {selectedOrg && (
        <OrganizationEventsModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          organizationId={selectedOrg.id}
          organizationName={selectedOrg.name}
          organizationLogoUrl={selectedOrg.logoUrl}
          schoolSlug={selectedOrg.schoolSlug}
          onMembershipChange={() => void mutate()}
        />
      )}
    </Box>
  );
};
